/**
 * Admin Estimates Page
 *
 * Paginated, searchable, sortable list of saved estimates with statistics.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Eye,
  MessageSquare,
  CalendarDays,
  TrendingUp,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Pencil,
} from 'lucide-react';
import { AdminEstimateEditModal, AdminEstimateEditable } from './AdminEstimateEditModal';
import { SPACE_TYPES } from '../../data/presets';
import { useAppSettings } from '../../context/AppSettingsContext';

interface Estimate {
  id: number;
  guid: string;
  estimate_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  user_category: string | null;
  project_type: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  postal_code: string | null;
  status: string;
  show_msrp_to_creator: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  is_deleted: boolean;
  space_type: string | null;
  calculation_mode: string | null;
  views: number;
  comments: number;
}

// Resolve the stored slug (e.g. "living-space") to its Title Case label
// ("Living Space"). Falls back to a slug → Title Case conversion if the
// slug isn't in SPACE_TYPES (e.g. legacy / removed types).
const formatSpaceType = (slug: string | null): string | null => {
  if (!slug) return null;
  const match = SPACE_TYPES.find(t => t.id === slug);
  if (match) return match.label;
  return slug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// Split a PascalCase value (e.g. "FullRoom") into space-separated words
// ("Full Room"). Returns null for empty input.
const formatCalculationMode = (value: string | null): string | null => {
  if (!value) return null;
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
};

// Combine calculation mode + space type as "Full Room | Garage".
// Either side may be missing; if both are missing, returns null.
const formatModeAndSpace = (mode: string | null, space: string | null): string | null => {
  const parts = [formatCalculationMode(mode), formatSpaceType(space)].filter(Boolean);
  return parts.length ? parts.join(' | ') : null;
};

interface Stats {
  today: number;
  yesterday: number;
  this_week: number;
  last_week: number;
  this_month: number;
  last_month: number;
  total: number;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

type SortField = 'id' | 'first_name' | 'estimate_name' | 'created_at' | 'city' | 'region' | 'views' | 'comments';

const API_BASE = '/api';

export const AdminEstimates: React.FC = () => {
  const { settings } = useAppSettings();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, per_page: 25, total: 0, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<SortField>('created_at');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [perPage, setPerPage] = useState(25);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<AdminEstimateEditable | null>(null);

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: perPage.toString(),
        search,
        sort,
        order,
      });
      if (showDeleted) params.set('include_deleted', '1');
      const res = await fetch(`${API_BASE}/admin-estimates.php?${params}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setEstimates(data.estimates);
        setPagination(data.pagination);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch estimates:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, perPage, search, sort, order, showDeleted]);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSort(field);
      setOrder('DESC');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const goToPage = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const formatDate = (dateStr: string) => {
    // created_at is stored as server-local wall-clock in the app timezone
    // (configurable in /admin/settings), so parse it as local and append the
    // configured zone's short label (e.g. EDT) to keep the time unambiguous.
    const d = new Date(dateStr.replace(' ', 'T'));
    const date = d.toLocaleDateString('en-US', {
      month: '2-digit', day: '2-digit', year: 'numeric',
    });
    const time = d.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
    let abbr = '';
    try {
      abbr = new Intl.DateTimeFormat('en-US', {
        timeZone: settings.timezone, timeZoneName: 'short',
      }).formatToParts(d).find(p => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      /* invalid tz — omit the label */
    }
    return `${date} ${time}${abbr ? ' ' + abbr : ''}`;
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sort !== field) return <ChevronDown size={12} className="opacity-25" />;
    return order === 'ASC' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ field, children, className }) => (
    <th
      className={`user-select-none ${className || ''}`}
      style={{ cursor: 'pointer' }}
      onClick={() => handleSort(field)}
    >
      <span className="d-inline-flex align-items-center gap-1">
        {children}
        <SortIcon field={field} />
      </span>
    </th>
  );

  // Pagination range
  const pageNumbers: number[] = [];
  const maxPages = 7;
  let startPage = Math.max(1, pagination.page - Math.floor(maxPages / 2));
  let endPage = Math.min(pagination.total_pages, startPage + maxPages - 1);
  if (endPage - startPage < maxPages - 1) {
    startPage = Math.max(1, endPage - maxPages + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className="row g-2 mb-3">
          {[
            { label: 'Today', value: stats.today, icon: CalendarDays, color: 'primary' },
            { label: 'Yesterday', value: stats.yesterday, icon: CalendarDays, color: 'secondary' },
            { label: 'This Week', value: stats.this_week, icon: TrendingUp, color: 'success' },
            { label: 'Last Week', value: stats.last_week, icon: TrendingUp, color: 'info' },
            { label: 'This Month', value: stats.this_month, icon: ClipboardList, color: 'warning' },
            { label: 'Last Month', value: stats.last_month, icon: ClipboardList, color: 'secondary' },
            { label: 'All Time', value: stats.total, icon: ClipboardList, color: 'dark' },
          ].map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="col-6 col-sm-4 col-lg">
                <div className={`bg-white rounded-3 border p-3 text-center h-100`}>
                  <Icon size={18} className={`text-${stat.color} mb-1`} />
                  <div className="fw-bold fs-4 text-dark">{stat.value}</div>
                  <div className="text-muted small">{stat.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Search + Per Page */}
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <form onSubmit={handleSearch} className="d-flex gap-2" style={{ maxWidth: 400, flex: 1 }}>
            <div className="input-group input-group-sm">
              <span className="input-group-text"><Search size={14} /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search name, email, project, GUID..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-sm btn-primary">Search</button>
            {search && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setSearchInput(''); }}>
                Clear
              </button>
            )}
          </form>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check form-switch mb-0" title="Include estimates that creators have deleted">
              <input
                id="show-deleted"
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={showDeleted}
                onChange={e => { setShowDeleted(e.target.checked); setPagination(prev => ({ ...prev, page: 1 })); }}
              />
              <label htmlFor="show-deleted" className="form-check-label small text-muted">Show deleted</label>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="small text-muted mb-0">Show:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
        {search && (
          <div className="mt-2 small text-muted">
            Showing results for "<strong>{search}</strong>" — {pagination.total} found
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover mb-0">
            <thead className="table-tc-slate">
              <tr>
                <SortableHeader field="id">ID</SortableHeader>
                <SortableHeader field="first_name">Name</SortableHeader>
                <th>GUID</th>
                <SortableHeader field="estimate_name">Project</SortableHeader>
                <SortableHeader field="city">Location</SortableHeader>
                <SortableHeader field="created_at">Date</SortableHeader>
                <SortableHeader field="views" className="text-center text-white"><Eye size={14} /></SortableHeader>
                <SortableHeader field="comments" className="text-center text-white"><MessageSquare size={14} /></SortableHeader>
                <th className="text-center text-white">Edit</th>
                <th className="text-center text-white">View</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center py-4 text-muted">Loading...</td>
                </tr>
              ) : estimates.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-4 text-muted">No estimates found.</td>
                </tr>
              ) : estimates.map(est => (
                <tr key={est.id} className={est.is_deleted ? 'table-warning' : ''}>
                  <td className="small">{est.id}</td>
                  <td>
                    <div className="fw-semibold small">{est.first_name} {est.last_name}</div>
                    {est.email && <div className="text-muted" style={{ fontSize: '0.75em' }}>{est.email}</div>}
                  </td>
                  <td>
                    <a
                      href={`/estimate/${est.guid}?source=admin`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary small font-monospace"
                      title={est.guid}
                    >
                      ...{est.guid.slice(-12)}
                    </a>
                  </td>
                  <td>
                    <div className="small">
                      {est.estimate_name || <span className="text-muted fst-italic">Untitled</span>}
                      {est.is_deleted && <span className="badge bg-danger ms-2">Deleted</span>}
                    </div>
                    {formatModeAndSpace(est.calculation_mode, est.space_type) && (
                      <div className="text-muted" style={{ fontSize: '0.75em' }}>{formatModeAndSpace(est.calculation_mode, est.space_type)}</div>
                    )}
                  </td>
                  <td className="small">
                    {est.city && est.region ? `${est.city}, ${est.region}` : est.city || est.region || '—'}
                  </td>
                  <td className="small text-nowrap">{formatDate(est.created_at)}</td>
                  <td className="text-center small">
                    <span className={est.views > 0 ? 'fw-bold text-dark' : 'text-muted'}>{est.views}</span>
                  </td>
                  <td className="text-center small">
                    <span className={est.comments > 0 ? 'fw-bold text-dark' : 'text-muted'}>{est.comments}</span>
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0 px-1"
                      title="Edit estimate admin settings"
                      onClick={() => setEditTarget({
                        id: est.id,
                        estimate_name: est.estimate_name,
                        first_name: est.first_name,
                        last_name: est.last_name,
                        email: est.email,
                        guid: est.guid,
                        show_msrp_to_creator: est.show_msrp_to_creator,
                        is_deleted: est.is_deleted,
                      })}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                  <td className="text-center">
                    <a
                      href={`/estimate/${est.guid}?source=admin`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary py-0 px-1"
                      title="Open estimate"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <AdminEstimateEditModal
          isOpen={editTarget !== null}
          estimate={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(next) => {
            setEstimates(prev => prev.map(e =>
              e.id === next.id
                ? {
                    ...e,
                    first_name: next.first_name,
                    last_name: next.last_name,
                    email: next.email,
                    estimate_name: next.estimate_name,
                    show_msrp_to_creator: next.show_msrp_to_creator,
                    is_deleted: next.is_deleted,
                    deleted_at: next.is_deleted ? e.deleted_at : null,
                  }
                : e
            ));
          }}
        />

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="small text-muted">
              Showing {((pagination.page - 1) * pagination.per_page) + 1}–{Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(1)}>First</button>
                </li>
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.page - 1)}>
                    <ChevronLeft size={14} />
                  </button>
                </li>
                {pageNumbers.map(n => (
                  <li key={n} className={`page-item ${n === pagination.page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(n)}>{n}</button>
                  </li>
                ))}
                <li className={`page-item ${pagination.page === pagination.total_pages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.page + 1)}>
                    <ChevronRight size={14} />
                  </button>
                </li>
                <li className={`page-item ${pagination.page === pagination.total_pages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.total_pages)}>Last</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEstimates;
